import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container, Page } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { Working } from '@ui/Working';
import {
  listGcsDocIngestionConfigs,
  syncGcsDocIngestionConfig,
  updateGcsDocIngestionConfig,
} from '@services/gcsDocIngestionConfigs';

const ListWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  overflow: hidden;
`;

const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const ConfigDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const SyncActionsRow = styled.div`
  margin-top: 12px;

  button {
    min-width: 120px;
  }
`;

const ConfigActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

const IngestionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  min-width: 232px;
  border-radius: 8px;
  background: ${({ theme, $enabled }) =>
    $enabled ? theme.themeColors.componentNoOpacity : theme.themeColors.appBackground};
  border: 1px solid
    ${({ theme, $enabled }) => ($enabled ? theme.themeColors.positive : theme.themeColors.lowContrastBorder)};
  box-shadow: ${({ theme, $enabled }) => ($enabled ? `0 1px 2px ${theme.themeColors.lowContrastBorder}` : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'not-allowed')};
  user-select: none;
  transition: border-color 150ms ease, box-shadow 150ms ease, filter 150ms ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 2px;
  }

  ${({ $interactive, theme, $enabled }) =>
    $interactive
      ? `
    &:hover {
      box-shadow: 0 2px 8px ${theme.themeColors.lowContrastBorder};
      border-color: ${$enabled ? theme.themeColors.positive : theme.themeColors.border};
    }
  `
      : ''}
`;

const StatusIndicator = styled.span`
  display: block;
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme, $enabled }) => ($enabled ? theme.themeColors.positive : theme.themeColors.secondaryText)};
`;

const StatusLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatusStateText = styled(Body5)`
  color: ${({ theme, $enabled }) => ($enabled ? theme.themeColors.positive : theme.themeColors.text)};
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error};
`;

const getGcsConfigUserEmail = (config) => {
  const fromUser = config?.user && typeof config.user === 'object' ? config.user.email : null;
  return config?.user_email || config?.email || fromUser || '';
};

function GcsDocIngestionConfigRow({
  config,
  canMutate,
  isSubmitting,
  isSyncing,
  toggleError,
  syncError,
  onToggle,
  onSync,
}) {
  const isEnabled = !!config.auto_doc_ingestion_enabled;
  const rowBusy = isSubmitting || isSyncing;
  const statusInteractive = canMutate && !rowBusy;
  const ownerEmail = getGcsConfigUserEmail(config).trim();

  const handleStatusActivate = React.useCallback(() => {
    if (!canMutate || rowBusy) {
      return;
    }
    onToggle(config);
  }, [canMutate, rowBusy, config, onToggle]);

  const statusAriaLabel = (() => {
    if (isSubmitting) {
      return 'Saving auto ingestion setting.';
    }
    const state = isEnabled ? 'enabled' : 'disabled';
    if (!canMutate) {
      return `Auto ingestion is ${state}. Only the bucket owner can change this.`;
    }
    return `Auto ingestion is ${state}. Click to turn ${isEnabled ? 'off' : 'on'}.`;
  })();

  return (
    <ConfigRow>
      <ConfigDetails>
        <Body5 bold>{config.bucket_name || 'Unnamed bucket'}</Body5>
        {ownerEmail ? (
          <Container top={4}>
            <Body5 isLowContrast>Email: {ownerEmail}</Body5>
          </Container>
        ) : null}
        {toggleError && (
          <Container top={8}>
            <ErrorText>{toggleError}</ErrorText>
          </Container>
        )}
        {syncError && (
          <Container top={8}>
            <ErrorText>{syncError}</ErrorText>
          </Container>
        )}
        <SyncActionsRow>
          <SkeletonButton disabled={!canMutate || rowBusy} onClick={() => onSync(config)}>
            {isSyncing ? 'Syncing...' : 'Sync now'}
          </SkeletonButton>
        </SyncActionsRow>
      </ConfigDetails>
      <ConfigActions>
        <IngestionStatus
          $enabled={isEnabled}
          $interactive={statusInteractive}
          role="button"
          tabIndex={statusInteractive ? 0 : -1}
          aria-label={statusAriaLabel}
          aria-busy={isSubmitting}
          aria-disabled={!statusInteractive}
          onClick={handleStatusActivate}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleStatusActivate();
            }
          }}
        >
          <StatusIndicator $enabled={isEnabled} aria-hidden />
          <StatusLabels>
            <Body7 isLowContrast>Auto ingestion</Body7>
            <StatusStateText bold $enabled={isEnabled}>
              {isSubmitting ? 'Saving...' : isEnabled ? 'Enabled' : 'Disabled'}
            </StatusStateText>
          </StatusLabels>
        </IngestionStatus>
        {!canMutate && (
          <Container top={4}>
            <Body5 isLowContrast>Only the owner can sync or change auto ingestion for this bucket.</Body5>
          </Container>
        )}
      </ConfigActions>
    </ConfigRow>
  );
}

export function GcsDocIngestionUploader({ pageWidth, onGoBack }) {
  const [configs, setConfigs] = React.useState([]);
  const [listError, setListError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [updatingConfigIds, setUpdatingConfigIds] = React.useState({});
  const [syncingConfigIds, setSyncingConfigIds] = React.useState({});
  const [rowErrors, setRowErrors] = React.useState({});
  const [syncRowErrors, setSyncRowErrors] = React.useState({});

  const fetchConfigs = React.useCallback(async (cancellationRef) => {
    setIsLoading(true);
    const { gcsDocIngestionConfigs, error } = await listGcsDocIngestionConfigs();

    if (cancellationRef?.isCanceled) {
      return;
    }

    setConfigs(gcsDocIngestionConfigs || []);
    setListError(error || null);
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    const cancellationRef = { isCanceled: false };
    fetchConfigs(cancellationRef);

    return () => {
      cancellationRef.isCanceled = true;
    };
  }, [fetchConfigs]);

  const handleToggle = React.useCallback(async (config) => {
    const nextValue = !config.auto_doc_ingestion_enabled;

    setRowErrors((prev) => ({ ...prev, [config.id]: null }));
    setUpdatingConfigIds((prev) => ({ ...prev, [config.id]: true }));

    const { gcsDocIngestionConfig, error } = await updateGcsDocIngestionConfig(config.id, nextValue);

    setUpdatingConfigIds((prev) => ({ ...prev, [config.id]: false }));

    if (gcsDocIngestionConfig) {
      setConfigs((prev) => prev.map((row) => (row.id === config.id ? gcsDocIngestionConfig : row)));
      return;
    }

    setRowErrors((prev) => ({
      ...prev,
      [config.id]: error || 'Failed to update config.',
    }));
  }, []);

  const handleSync = React.useCallback(async (config) => {
    setSyncRowErrors((prev) => ({ ...prev, [config.id]: null }));
    setSyncingConfigIds((prev) => ({ ...prev, [config.id]: true }));

    const { gcsDocIngestionConfig, error } = await syncGcsDocIngestionConfig(config.id);

    setSyncingConfigIds((prev) => ({ ...prev, [config.id]: false }));

    if (gcsDocIngestionConfig?.id != null) {
      setConfigs((prev) => prev.map((row) => (row.id === config.id ? gcsDocIngestionConfig : row)));
      return;
    }

    if (error) {
      setSyncRowErrors((prev) => ({
        ...prev,
        [config.id]: error,
      }));
    }
  }, []);

  return (
    <Page width={pageWidth}>
      <BackArrowButton onClick={onGoBack} />
      <Container top={36}>
        <Body1 bold>GCS Document Ingestion</Body1>
        <Container top={8}>
          <Body5 isLowContrast>View each configured GCS bucket.</Body5>
        </Container>

        {isLoading ? (
          <Container top={24}>
            <Working noVerticalPadding message="Loading configs" />
          </Container>
        ) : listError ? (
          <Container top={24}>
            <ErrorText>{listError}</ErrorText>
            <Container top={12}>
              <SkeletonButton onClick={() => fetchConfigs()}>Retry</SkeletonButton>
            </Container>
          </Container>
        ) : configs.length === 0 ? (
          <Container top={24}>
            <Body5 isLowContrast>No GCS ingestion configs found.</Body5>
          </Container>
        ) : (
          <Container top={24}>
            <ListWrapper>
              {configs.map((config) => (
                <GcsDocIngestionConfigRow
                  key={config.id}
                  config={config}
                  canMutate={Boolean(config.is_owner)}
                  isSubmitting={!!updatingConfigIds[config.id]}
                  isSyncing={!!syncingConfigIds[config.id]}
                  toggleError={rowErrors[config.id]}
                  syncError={syncRowErrors[config.id]}
                  onToggle={handleToggle}
                  onSync={handleSync}
                />
              ))}
            </ListWrapper>
          </Container>
        )}
      </Container>
    </Page>
  );
}
